import { BiX } from 'react-icons/bi'
import { Box, Text, VStack } from '@chakra-ui/react'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { IconButton, useToast } from '@opengovsg/design-system-react'
import { useMutation } from '@tanstack/react-query'

import { FEATURE_FLAGS } from '~shared/constants'
import { GetEventRes, SubmitAdminCsatReq } from '~shared/dto'

import { api } from '~lib/api'
import { CsatForm } from '~components/CsatForm'

import {
  isInCsatCooldown,
  markCsatShown,
  useAdminCsatStore,
} from '~features/events/adminCsatStore'

export interface AdminCsatModalProps {
  event: GetEventRes
}

/**
 * CSAT overlay for the event builder. A corner card, not a Modal, so it
 * never blocks builder actions. Opens when a trigger fires - the event's
 * open/closed status is toggled and saved, or its link is shared (header
 * copy button or the Share modal) - provided the flag is on, the event has
 * at least one schedule (proxy for "real event, not a 30-second draft") and
 * the 28-day cooldown has lapsed. Dismiss and submit both start the cooldown.
 */
export const AdminCsatModal = ({ event }: AdminCsatModalProps) => {
  const isEnabled = useFeatureIsOn(FEATURE_FLAGS.ADMIN_CSAT)
  const pendingTrigger = useAdminCsatStore((state) => state.pendingTrigger)
  const clearTrigger = useAdminCsatStore((state) => state.clearTrigger)
  const toast = useToast()

  const { mutate, isLoading } = useMutation(
    (body: SubmitAdminCsatReq) =>
      api.url(`/admin/events/${event.id}/csat`).post(body).res(),
    {
      onSettled: () => {
        toast({
          status: 'success',
          description: 'Thank you for your feedback!',
        })
        handleClose()
      },
    },
  )

  const isOpen =
    isEnabled &&
    pendingTrigger !== null &&
    event.schedules.length >= 1 &&
    !isInCsatCooldown()

  const handleClose = () => {
    markCsatShown()
    clearTrigger()
  }

  if (!isOpen || pendingTrigger === null) return null

  return (
    <Box
      position="fixed"
      bottom={{ base: 4, md: 6 }}
      right={{ base: 4, md: 6 }}
      w={{ base: 'calc(100vw - 2rem)', md: '360px' }}
      bg="white"
      borderRadius="8px"
      boxShadow="0 4px 16px rgba(0, 0, 0, 0.16)"
      p={5}
      zIndex="popover"
    >
      <IconButton
        aria-label="Dismiss feedback prompt"
        variant="clear"
        size="xs"
        position="absolute"
        top={2}
        right={2}
        icon={<BiX fontSize="1.25rem" />}
        onClick={handleClose}
      />
      <VStack alignItems="start" spacing={3}>
        <Text textStyle="subhead-1" pr={6}>
          How is your experience using CalSG?
        </Text>
        <CsatForm
          isSubmitting={isLoading}
          onSubmit={({ score, comment }) =>
            mutate({ score, comment, triggerSource: pendingTrigger })
          }
        />
      </VStack>
    </Box>
  )
}
